// Shared `--grid` context-flag plumbing (org→grid vocab migration).
//
// CloudGrid's user-facing vocabulary is "grid", not "org" (the CLI has no
// billing-org concept — every user-visible "org" means a grid). The context
// flag that picks/overrides the grid for a single call is therefore `--grid`.
//
// Back-compat is MANDATORY: the published 0.11.0 CLI and team scripts pass
// `--org <slug>`, so `--org` must keep working. Commander does not alias
// options natively, so every command that takes a grid-context flag defines
// BOTH options — `--grid` (primary, shown in --help) and `--org` (deprecated,
// hidden alias) — and reads the effective value through {@link gridSlug}.
//
// Follow-up (noted in the PR body): the internal module `active-org.ts` and
// its `*Org*` symbols are NOT renamed in this pass to keep churn contained;
// only the user-facing flag + strings change here.

import { Command, Option } from 'commander';

/** Opts shape shared by every command that accepts a grid-context flag. */
export interface GridOpts {
  /** The primary `--grid <slug>` value. */
  grid?: string;
  /** Deprecated back-compat alias `--org <slug>`. Prefer {@link GridOpts.grid}. */
  org?: string;
}

/**
 * Attach the grid-context flags to a command: `--grid <slug>` as the primary,
 * documented option and `--org <slug>` as a hidden, deprecated back-compat
 * alias. Use this instead of hand-rolling `.option('--org ...')` so every
 * command exposes the pair consistently.
 *
 * @param command   the commander Command to mutate (returned for chaining)
 * @param description  the help text for the primary `--grid` option
 */
export function addGridOption(command: Command, description: string): Command {
  command.option('--grid <slug>', description);
  // The alias is hidden from --help but still parses and resolves. We keep a
  // terse description for anyone who inspects it programmatically.
  command.addOption(
    new Option('--org <slug>', 'Deprecated alias for --grid').hideHelp(),
  );
  return command;
}

let deprecationNoticeShown = false;

/**
 * Resolve the effective grid slug from parsed options, preferring the primary
 * `--grid` flag and falling back to the deprecated `--org` alias.
 *
 * When only `--org` is passed, a one-line deprecation note is written to
 * stderr (once per process, so a multi-call flow doesn't spam it). `--org`
 * always still resolves — it is never an error.
 *
 * Returns `undefined` when neither flag is set, so callers can pass the result
 * straight into `getV2Client(...)` / active-grid resolution unchanged.
 */
export function gridSlug(opts: GridOpts): string | undefined {
  if (opts.grid !== undefined) return opts.grid;
  if (opts.org !== undefined) {
    if (!deprecationNoticeShown) {
      deprecationNoticeShown = true;
      process.stderr.write('Note: --org is deprecated; use --grid.\n');
    }
    return opts.org;
  }
  return undefined;
}

/** Test-only reset of the once-per-process deprecation-note latch. */
export function __resetGridDeprecationNotice(): void {
  deprecationNoticeShown = false;
}
